import React, { useState, useEffect, useCallback, useRef } from 'react';
import LicenseVerificationPanel from '../components/admin/LicenseVerificationPanel';
import PartnerOverviewState from '../components/admin/PartnerOverviewState';
import BrokerOfRecordField from '../components/admin/BrokerOfRecordField';
import PartnerLicenseFields from '../components/admin/PartnerLicenseFields';
import { SOURCE_LABEL } from '../constants/leadSources';
import { displayPhone, telHref } from '../utils/phone';
import { stateName } from '../constants/usStates';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import StaffComposer from '../components/admin/StaffComposer';
import RichMessageBody, { hasRichBody } from '../components/RichMessageBody';
import MessageAttachment from '../components/MessageAttachment';
import RightDrawer from '../components/RightDrawer';
import { useTableSort } from '../hooks/useTableSort';
import StatCard from '../components/StatCard';
import KpiDelta from '../components/KpiDelta';
import PartnerInviteModal from '../components/PartnerInviteModal';
import { countBeforeThisMonth } from '../components/kpiMonth';
import { Icon } from '../utils/icons';
import { useNavigate } from 'react-router-dom';

import OwnerCell from '../components/OwnerCell';

import { LICENSED_STATE_CODES } from '../constants/marketExpansion';
import { useStaffRole } from '../hooks/useStaffRole';
// 🔑 What a partner's files are called (Jesse, 2026-11-02): "Submissions" for a loan officer, "Referrals" for a
// realtor or builder. The same rule the server's utils/partnerFileCounts.fileLabelForType applies.
const filesLabelFor = (t) => (t === 'loan_officer' ? 'Submissions' : (t === 'realtor' || t === 'homebuilder') ? 'Referrals' : 'Files');
const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_STYLES = {
  active:    { bg: '#acf44e', color: '#1a1a1a' },
  pending:   { bg: '#fffbeb', color: '#7a5200' },
  archived:  { bg: '#f3f4f6', color: '#9ca3af' },
  // ⚠️ "Invited" IS NOT "Pending". Pending means a partner registered and has
  // not finished onboarding. (An invited person has no account at all, and now
  // appears on the Leads page rather than here.) They were
  // the same word for as long as invites were invisible, and merging them hides
  // exactly the row a rep needs to chase.
};



const REFERRAL_STATUS_STYLES = {
  pending:               { bg: '#fffbeb', color: '#7a5200' },
  registered:            { bg: '#64748B', color: '#ffffff' },  // Slate
  applied:               { bg: '#0EA5E9', color: '#1a1a1a' },  // Cyan
  approved:              { bg: '#acf44e', color: '#1a1a1a' },  // Doorly green
  home_shopping:         { bg: '#7C58F5', color: '#ffffff' },  // Violet
  under_contract:        { bg: '#14B8A6', color: '#1a1a1a' },  // Teal
  closing:               { bg: '#14B8A6', color: '#1a1a1a' },  // Teal
  closed:                { bg: '#111827', color: '#ffffff' },  // Dark
};

// ⚠️ THE DEFAULT STATE COMES FROM THE MIRROR, not a literal 'TX'. A default is
// the value most rows will carry, so a stale one is wrong on every new record
// rather than on an edge case.
const BLANK_FORM = { name: '', company: '', email: '', phone: '', type: 'realtor', state: LICENSED_STATE_CODES[0] || '', status: 'pending', notes: '' };

function fmtRevenue(v) {
  if (!v) return '$0';
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  return `$${v.toLocaleString()}`;
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(typeof d === 'string' && d.length === 10 ? d + 'T12:00:00' : d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusBadge({ status, styles }) {
  const s = styles[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 9px', borderRadius: '9999px', backgroundColor: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
}

// ⚠️ href IS OPTIONAL AND FALLS BACK TO TEXT. A row with nothing to link to must
// render exactly as it did — the link is an affordance on some values, not a new
// shape for every row.
function InfoRow({ label, value, href, note }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      {href && value ? (
        // 🔑 A tel: LINK, WHICH IS WHAT MAKES IT TAPPABLE ON A PHONE. On a desktop
        // browser it opens whatever handler is registered and is otherwise inert,
        // so it costs nothing there and is the whole feature on mobile.
        <a href={href} style={{ fontSize: '13px', color: '#3a6ad9', fontWeight: '600', textDecoration: 'none' }}>{value}</a>
      ) : (
        <span style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>{value || '—'}</span>
      )}
      {note && (
        <span style={{ fontSize: '11px', color: '#9ca3af' }}>{note}</span>
      )}
    </div>
  );
}

export default function AdminREPartners() {
  // 🔑 ADMIN-ONLY CONTROLS ON THIS PAGE. The ownership block READS for every
  // role that can open the drawer (reassign-impact is allow-listed for
  // partner_success deliberately — it is their panel); only the WRITE is
  // admin-only.
  const { isAdmin: isAdminStaff } = useStaffRole();
  const token = localStorage.getItem('doorly_admin_token');

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  // ⚠️ TIER IS GONE. The dropdown offered Standard / Gold / Platinum and no such
  // field exists anywhere in the backend — the only writer was a hardcoded
  // 'Standard' on the optimistic row below, so Gold and Platinum could never
  // match anything. A filter that always returns nothing looks like a filter
  // with no results, which is the "looks like data and isn't" shape. Replaced
  // with Owner, which is real and is now the column people will filter on.
  const [ownerFilter, setOwnerFilter] = useState('All');
  // ── 🔑 SORTING MOVED TO THE SHARED HOOK ───────────────────────────────────
  //
  // Jesse: "i want to be able to sort the Partners Leads, Partners, and accounts
  // page tables." This page already sorted its three NUMERIC columns and said
  // why the others were left out: "Sorting Company or Status alphabetically is a
  // different feature with different edge cases (nulls, case, the 'Type not set'
  // badge)."
  //
  // 🔑 THAT WAS THE RIGHT CALL AND IT IS NOW SPENT. hooks/useTableSort handles
  // exactly those three: blanks sort last in both directions, text compares
  // case-insensitively, and numbers compare numerically. So the text columns can
  // be offered without the arrow lying about what it will do.
  const SORT_COLUMNS = React.useMemo(() => ({
    name:       (p) => p.name,
    type:       (p) => p.partner_type,
    company:    (p) => p.company,
    owner:      (p) => p.owner_name,
    status:     (p) => p.status,
    state:      (p) => p.license_state,
    referrals:  { get: (p) => p.referrals, type: 'number' },
    closings:   { get: (p) => p.closings, type: 'number' },
    revenue:    { get: (p) => p.revenue, type: 'number' },
    last_activity: { get: (p) => p.last_activity_at, type: 'date' },
  }), []);
  const { sortKey, sortDir, toggle: toggleSort, sortRows } = useTableSort(SORT_COLUMNS);
  const [stateFilter, setStateFilter] = useState('All');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [partnerMsgs, setPartnerMsgs] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgConvId, setMsgConvId] = useState(null);
  // 🔑 THE DRIP LOG AND THE ACTIVATION LEDGER, loaded with the Messages tab.
  // Two different questions — what was sent, and where they are in the sequence
  // — so two pieces of state rather than one shape that has to mean both.
  const [dripEmails, setDripEmails] = useState([]);
  const [activationSteps, setActivationSteps] = useState([]);
  const msgEndRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [agreementData, setAgreementData] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaveMsg, setEditSaveMsg] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [showCog, setShowCog] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [archivedPartners, setArchivedPartners] = useState([]);
  const cogRef = useRef(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  // ⚠️ kind DEFAULTS EXPLICITLY AND IS RESTORED ON RESET. Left out, inviteForm.kind
  // is undefined until a rep clicks one, and the toggle renders with neither
  // option selected — so the commonest invite goes out with no visible choice
  // made. The server also defaults to 'partner', but a UI that shows no selection
  // is a UI a rep will click something in just to make it look answered.
  // ⚠️ THE INVITE FORM'S STATE MOVED WITH THE FORM. It lived here — blank form,
  // batch text, preview, busy, success, error — beside a second copy of all of it
  // in AccountInviteModal. components/PartnerInviteModal owns it now; what stays
  // on this page is the ROW-LEVEL invite actions (resend, cancel), which are
  // about existing invites rather than sending new ones.
  const [partnerReferrals, setPartnerReferrals] = useState([]);
  // The partner's files (referrals + broker submissions) with each row's staff link; null = the server sent none.
  const [partnerFiles, setPartnerFiles] = useState(null);
  const navigate = useNavigate();
  const [docUploading, setDocUploading] = useState(null);
  const [docUploadMsg, setDocUploadMsg] = useState(null);
  // Ownership / reassignment. Loaded per-partner when the drawer opens.
  const [ownership, setOwnership] = useState(null);      // null = not loaded
  // ⚠️ null = not loaded, { found:false } = loaded and there is no lead. Those
  // are different answers and the drawer renders them differently.
  const [origin, setOrigin] = useState(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [reassignBusy, setReassignBusy] = useState(false);
  const [reassignErr, setReassignErr] = useState('');
  // Invite row actions (resend / cancel), keyed by invite id so one busy row
  // does not disable the others.
  // ⚠️ THE INVITE RESEND/CANCEL CONTROLS WENT WITH THE ROWS. They acted on a
  // partner_invites row rendered in this table; with those rows on the Leads page
  // the actions belong beside them there, not here on a list of people who have
  // accounts.
  const [showPartnerDocModal, setShowPartnerDocModal] = useState(false);
  const [viewingPartnerDoc, setViewingPartnerDoc] = useState(null);

  // ⚠️ EVERY FIELD IS REQUIRED, MIRRORING THE SERVER. This checked name + email
  // only, which is how partners arrived with no type — and partner_type carries a
  // 2% agreement obligation nothing downstream can safely invent. The server is
  // still the gate; this only saves a round trip.
  const fetchPartners = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetched = res.data || [];

      // ⚠️ PENDING INVITES ARE ROWS IN THIS TABLE, THE WAY A PENDING STAFF INVITE
      // IS A ROW IN THE STAFF TABLE. Before this they were invisible: a rep sent
      // an invite and the invitee did not exist anywhere in DoorlyDesk until they
      // registered, so there was nothing to chase, resend or withdraw.
      //
      // ⚠️ THE MERGE IS NOT FATAL. A failure to load invites must not empty the
      // partner list — the partners are the load-bearing half, and an invite the
      // rep cannot see today is the status quo, not a regression.
      // 🚨 PENDING INVITES MOVED TO THE LEADS PAGE. Partners and Accounts now
      // mean REGISTERED; everything before registration lives on
      // /admin/partners/leads, where the invite is a stage of one lead rather
      // than a ghost row in a table of people who have accounts.
      //
      // ⚠️ THE ENTERPRISE TEAM VIEW KEEPS ITS PENDING INVITES. That is DoorlyPro,
      // and a broker asking "who have I invited to my firm" is a different
      // question from a rep asking "who are my partners".
      setPartners(fetched);
      // Refresh selectedPartner if open
      setSelectedPartner((sp) => {
        if (!sp) return sp;
        const updated = fetched.find(p => p.id === sp.id || p.email === sp.email);
        return updated ? { ...sp, ...updated } : sp;
      });
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  // Close cog dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (cogRef.current && !cogRef.current.contains(e.target)) setShowCog(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openPartner = (p) => {
    setSelectedPartner(p);
    setActiveTab('overview');
    setAgreementData(null);
    setPartnerReferrals([]);
    setPartnerFiles(null);
    setPartnerMsgs([]); setMsgConvId(null); // the composer's draft resets by its key (the partner id)
    // ⚠️ CLEARED WITH THE REST. Left behind, the previous partner's drip log
    // renders under the next partner's name for as long as the fetch takes —
    // which is the worst kind of wrong, because it is readable and plausible.
    setDripEmails([]); setActivationSteps([]);
    setEditForm({});
    setEditSaveMsg(null);
    setArchiveConfirm(false);
  };

  // 🔑 THE LICENCE-CHECK EMAIL'S LINK (backend utils/licenceCheckAlerts): ?partner=<id>&panel=licence opens that
  // partner's drawer at the License verification panel, where "Record a check" is. Once per page load. A [TEST]
  // partner is outside the default list, so a miss is looked up again with include_test before giving up.
  const deepLinkDone = useRef(false);
  const [scrollToLicence, setScrollToLicence] = useState(false);
  useEffect(() => {
    if (deepLinkDone.current || loading || !token) return;
    const qs = new URLSearchParams(window.location.search);
    const want = Number(qs.get('partner'));
    if (!want) return;
    const open = (p) => {
      deepLinkDone.current = true;
      openPartner(p);
      if (qs.get('panel') === 'licence') setScrollToLicence(true);
    };
    const hit = partners.find((p) => Number(p.id) === want);
    if (hit) { open(hit); return; }
    if (!partners.length) return;
    deepLinkDone.current = true;
    axios.get(`${API}/api/admin/partners?include_test=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { const p = (r.data || []).find((x) => Number(x.id) === want); if (p) open(p); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners, loading, token]);
  // ⚠️ WAIT FOR THE PANEL, NOT A TIMER (E2E 224, sharded run 41247145682): a 300 ms timer scrolled before the panel had
  // loaded, and the cards above it (overview state, origin) grow after their own fetches and push it back out of view.
  // So: watch the DOM until the panel has rendered its "Record a check", scroll to it, and keep it in view while the
  // drawer above it settles (1.5 s with no change) — stopping at once if the person scrolls or touches the page.
  useEffect(() => {
    if (!scrollToLicence || !selectedPartner?.id) return undefined;
    let quiet = null; let done = false;
    const ready = () => {
      const el = document.getElementById('licence-verification');
      return el && /Record a check/.test(el.innerText || '') ? el : null;
    };
    const teardown = () => {
      if (done) return;
      done = true;
      obs.disconnect(); clearTimeout(quiet); clearTimeout(cap);
      ['wheel', 'touchstart', 'keydown'].forEach((e) => window.removeEventListener(e, finish, true));
    };
    const finish = () => { if (!done) { teardown(); setScrollToLicence(false); } };
    const place = () => {
      const el = ready();
      if (!el) return;
      el.scrollIntoView({ block: 'start' });
      clearTimeout(quiet);
      quiet = setTimeout(finish, 1500);
    };
    const obs = new MutationObserver(place);
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    ['wheel', 'touchstart', 'keydown'].forEach((e) => window.addEventListener(e, finish, true));
    const cap = setTimeout(finish, 20000);
    place();
    return teardown;
  }, [scrollToLicence, selectedPartner?.id]);

  // ⚠️ WHO OWNS THIS PARTNER, AND WHAT A HANDOVER WOULD TAKE WITH IT.
  // Loaded whenever the drawer opens rather than on the reassign click, because
  // the current holder belongs on the overview whether or not anyone is about to
  // move them — and a rep reading a partner page should be able to see it.
  useEffect(() => {
    if (!selectedPartner?.id) { setOwnership(null); return; }
    let cancelled = false;
    setOwnership(null);
    axios.get(`${API}/api/admin/partners/${selectedPartner.id}/reassign-impact`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!cancelled) setOwnership(r.data); })
      // ⚠️ AN ERROR IS ITS OWN STATE, NOT AN EMPTY ONE. "Nobody owns this" and
      // "we could not find out" look identical if a failure sets null.
      .catch((e) => { if (!cancelled) setOwnership({ error: e.response?.data?.error || e.message }); });
    return () => { cancelled = true; };
  }, [selectedPartner?.id, token]);

  // ── WHERE THIS PARTNER CAME FROM, read when the drawer opens ──────────────
  //
  // 🚨 SEPARATE STATE FROM "NOT LOADED", for the same reason the ownership block
  // above distinguishes its three cases: an absent lead and a failed fetch look
  // identical if both set null, and one of them means "this partner predates the
  // funnel" while the other means "ask again".
  useEffect(() => {
    if (!selectedPartner?.id) { setOrigin(null); return; }
    let cancelled = false;
    setOrigin(null);
    axios.get(`${API}/api/admin/partners/${selectedPartner.id}/origin`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!cancelled) setOrigin(r.data); })
      .catch((e) => { if (!cancelled) setOrigin({ error: e.response?.data?.error || e.message }); });
    return () => { cancelled = true; };
  }, [selectedPartner, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitReassign = async () => {
    if (!selectedPartner?.id || !reassignTo) return;
    setReassignBusy(true); setReassignErr('');
    try {
      const r = await axios.post(`${API}/api/admin/partners/${selectedPartner.id}/reassign`,
        { to_staff_id: Number(reassignTo) },
        { headers: { Authorization: `Bearer ${token}` } });
      const d = r.data || {};
      // ⚠️ REPORT WHAT MOVED, INCLUDING THE IN-FLIGHT COUNT THE SERVER SAW. The
      // preview and the confirmation quoting the same number is the only check an
      // admin has that the thing they were shown is the thing that happened.
      alert(`Reassigned. ${d.movedTickets ?? 0} open ticket(s) moved; ${d.inFlightAtHandover ?? 0} file(s) past submission went with the account.`);
      setReassignOpen(false); setReassignTo('');
      const rr = await axios.get(`${API}/api/admin/partners/${selectedPartner.id}/reassign-impact`,
        { headers: { Authorization: `Bearer ${token}` } });
      setOwnership(rr.data);
    } catch (e) {
      setReassignErr(e.response?.data?.error || e.message);
    } finally { setReassignBusy(false); }
  };

  // Fetch referrals for selected partner
  useEffect(() => {
    if (activeTab !== 'referrals' || !selectedPartner?.id) return;
    axios.get(`${API}/api/doorlypro/admin/partners/${selectedPartner.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => { setPartnerReferrals(res.data?.referrals || []); setPartnerFiles(Array.isArray(res.data?.files) ? res.data.files : null); })
      .catch(() => { setPartnerReferrals([]); setPartnerFiles(null); });
  }, [activeTab, selectedPartner, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'messages' || !selectedPartner?.id) return;
    setMsgLoading(true);
    axios.get(`${API}/api/admin/partners/${selectedPartner.id}/messages`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setPartnerMsgs(r.data.messages || []);
        setMsgConvId(r.data.conversation?.id || null);
        setDripEmails(r.data.dripEmails || []);
        setActivationSteps(r.data.activationSteps || []);
      })
      .catch(() => { setPartnerMsgs([]); setDripEmails([]); setActivationSteps([]); })
      .finally(() => setMsgLoading(false));
  }, [activeTab, selectedPartner, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔑 THE ONE STAFF COMPOSER (fp-format, Jesse 2026-11-01): payload = { body, format: 'rich', attachment_data?,
  // attachment_name? } from StaffComposer. The thread reply and the first-message route both read it through
  // utils/staffMessageCompose and dual-write the partner's copy. Resolves true when sent; a refusal throws.
  const sendPartnerMsg = async (payload) => {
    if (!selectedPartner) return false;
    try {
      if (msgConvId) {
        const r = await axios.post(`${API}/api/admin/messages/threads/${msgConvId}/reply`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setPartnerMsgs(prev => [...prev, r.data.message]);
      } else {
        const r = await axios.post(`${API}/api/admin/partners/${selectedPartner.id}/messages`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setPartnerMsgs(prev => [...prev, r.data.message]);
        setMsgConvId(r.data.conversation?.id);
      }
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      return true;
    } catch (e) {
      throw new Error(e.response?.data?.error || 'The message was not sent.');
    }
  };

  useEffect(() => {
    if (activeTab !== 'agreements' || !selectedPartner) return;
    axios
      .get(`${API}/api/doorlypro/admin/partners/${selectedPartner.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data;
        // Find most recent agreement_signature document
        let sigData = null;
        if (Array.isArray(data.documents)) {
          const sigDoc = data.documents
            .filter((d) => d.doc_type === 'agreement_signature')
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
          if (sigDoc) sigData = sigDoc.file_path;
        }
        setAgreementData({ ...data, signature_data: sigData });
      })
      .catch(() => setAgreementData(null));
  }, [activeTab, selectedPartner, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/partners`, addForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
    const newPartner = {
      ...addForm,
      id: 'p_' + Date.now(),
      referrals: 0, closings: 0, revenue: 0,
      tier: 'Standard', lastActivity: new Date().toISOString().slice(0, 10),
      license: '', nmls: null, w9: 'Pending', agreement: 'Pending', brokerage_id: null, notes: addForm.notes,
    };
    setPartners((prev) => [...prev, newPartner]);
    setShowAddModal(false);
    setAddForm(BLANK_FORM);
    setSaving(false);
  };

  // A sentinel rather than '' so it cannot collide with a real (empty) name and
  // so the <option> has something to render.
  const NO_OWNER = '__none__';

  // ⚠️ BOTH DROPDOWNS ARE DERIVED FROM THE ROWS, NOT HARDCODED. The State filter
  // used to be the literal list ['All', 'TX'] — so a partner licensed anywhere
  // else was unfilterable and invisible to that control, and nobody would learn
  // that from looking at it.
  const ownerOptions = React.useMemo(() => Array.from(new Set(
    partners.map((p) => (p.owner_name || '').trim()).filter(Boolean))).sort(), [partners]);
  const stateOptions = React.useMemo(() => Array.from(new Set(
    partners.map((p) => (p.state || p.license_state || '').trim()).filter(Boolean))).sort(), [partners]);

  const filtered = partners.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || p.company.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'archived' ? p.status === 'archived' : statusFilter === 'All' ? p.status !== 'archived' : p.status === statusFilter;
    // ⚠️ NO OWNER IS A FILTERABLE ANSWER, NOT THE ABSENCE OF ONE. Four partners
    // have no assignment row at all; being able to select exactly those is how
    // somebody notices and fixes it, rather than scrolling for a blank cell.
    const owner = (p.owner_name || '').trim();
    const matchOwner = ownerFilter === 'All'
      || (ownerFilter === NO_OWNER ? !owner : owner === ownerFilter);
    const matchState = stateFilter === 'All' || (p.state || p.license_state) === stateFilter;
    return matchSearch && matchStatus && matchOwner && matchState;
  });

  // ⚠️ SORTED AFTER FILTERING, AND ONLY WHEN ASKED. With no sort key the server's
  // order (newest first) is preserved — a table that silently reorders itself on
  // load makes "the row I was just looking at" hard to find again.
  // ⚠️ SORTED AFTER FILTERING, AND ONLY WHEN ASKED — unchanged. With no sort key
  // sortRows returns the array untouched, so the server's order (newest first)
  // is preserved and the third click still gets back to it.
  const sorted = React.useMemo(() => sortRows(filtered), [filtered, sortRows]);

  const totalCount   = partners.filter(p => p.status !== 'archived').length;
  const activeCount  = partners.filter((p) => p.status === 'active').length;
  const pendingCount = partners.filter((p) => p.status === 'pending').length;
  // ⚠️ THE SAME ARRAY THE TABLE RENDERS, so the card cannot disagree with the
  // list. Invite rows no longer appear here at all — see the Leads page.

  const STATUS_PILLS = ['All', 'active', 'pending'];
  const TABS = ['overview', 'referrals', 'messages', 'documents', 'profile', 'archive'];

  return (
    <AdminLayout>
      <div className="admin-page-body" style={{ backgroundColor: '#f9fafb', minHeight: '100vh' }}>
        {/* ── 🚨 THE HEADER SQUEEZED THE SUBTITLE INTO A COLUMN OF WORDS ──────
            Jesse, on mobile: "The 'Manage real estate agent…' copy is wrapping
            on multiple lines and pushing the rest of the page content down."

            `justify-content: space-between` with a text block on the left and
            two buttons on the right gives the text whatever the buttons do not
            want — about 150px on a phone — so a 95-character sentence wrapped to
            six lines and everything below moved down by ~100px.

            🔑 SO THE HEADER STACKS ON MOBILE and the subtitle gets the full
            width, which is two lines rather than six. The copy is not cut: it is
            the one sentence that says what the page is for, and a reader on a
            phone needs it as much as one on a monitor. */}


        {/* Header */}
        <div className="admin-page-head">
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Partners</h1>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>Manage real estate agent, mortgage, and homebuilder referral partners and track performance.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => setShowInviteModal(true)}
              style={{ padding: '9px 18px', borderRadius: '9px', border: 'none', backgroundColor: '#3a6ad9', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              {/* ⚠️ "+ Invite Partner", matching Accounts and Leads. Three pages
                  in one section reading "Invite Partner", "+ Invite Account" and
                  "Invite +" is three different buttons to a reader. */}
              + Invite Partner
            </button>
            <div ref={cogRef} style={{ position: 'relative' }}>
              <button onClick={() => setShowCog(c => !c)}
                style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Settings">⚙️</button>
              {showCog && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 200, zIndex: 500, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 14px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f3f4f6' }}>Partner Management</div>
                  <button onClick={() => {
                    setShowCog(false);
                    const archived = partners.filter(p => p.status === 'archived');
                    setArchivedPartners(archived);
                    setShowArchivedModal(true);
                  }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    📦 Archived Partners
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stat Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {/* ⚠️ THE SAME BASELINE RULE AS ACCOUNTS AND REFERRALS — counted from
              created_at on the rows this page already has, so a card cannot
              disagree with the list beneath it and test exclusion comes free.
              These cards previously carried no secondary line at all, which made
              them the only KPI row on the tab with nothing under the number. */}
          <StatCard label="Total Partners" value={totalCount} icon={<Icon name="users" size={18} color="#3a6ad9" />} bg="#eff4ff" color="#3a6ad9">
            <KpiDelta value={totalCount} prev={countBeforeThisMonth(partners, (p) => p.status !== 'archived')} />
            {/* 🚨 WHY THIS DOES NOT MATCH THE ACCOUNTS PAGE'S "Partners" CARD, said
                on the card rather than left as arithmetic staff cannot do. That one
                sums members across the ACCOUNTS it shows, so a partner with no firm
                yet is invisible to it — a real difference, not a counting bug. Test
                records are now excluded from both by the same rule. */}
            <div style={{ marginTop: 2, fontSize: 10, color: '#9ca3af' }}>Active + Pending</div>
          </StatCard>
          <StatCard label="Active" value={activeCount} icon={<Icon name="check-circle" size={18} color="#acf44e" />} bg="#f0fde4" color="#acf44e">
            <KpiDelta value={activeCount} prev={countBeforeThisMonth(partners, (p) => p.status === 'active')} />
          </StatCard>
          {/* ⚠️ NO DELTA ON A QUEUE. "Pending onboarding" is a state people LEAVE,
              so a creation baseline would read a successful month — everybody
              finishing — as a fall, which is the opposite of what happened. */}
          <StatCard label="Pending Onboarding" value={pendingCount} icon={<Icon name="clock" size={18} color="#7a5200" />} bg="#fffbeb" color="#7a5200">
            <KpiDelta note="Not yet finished onboarding" />
          </StatCard>
          {/* 🚨 THE INVITED CARD IS GONE WITH THE ROWS IT COUNTED. Total is now
              Active + Pending, which still adds up on sight — what changed is
              that an invited person is not a partner until they register, and
              their card is on the Leads page. */}
        </div>

        {/* Filter Bar */}
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '14px 16px', marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search partners…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ height: '36px', padding: '0 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', width: '200px' }}
          />
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {STATUS_PILLS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                style={{ padding: '5px 13px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600', border: '1px solid', cursor: 'pointer', backgroundColor: statusFilter === s ? '#3a6ad9' : '#fff', borderColor: statusFilter === s ? '#3a6ad9' : '#e5e7eb', color: statusFilter === s ? '#fff' : '#374151', textTransform: 'capitalize' }}
              >
                {s}
              </button>
            ))}
          </div>
          {/* ⚠️ BOTH DROPDOWNS ARE LABELLED. They were two bare selects sitting
              next to each other, so the only way to know which was which was to
              open one — and one of them ("Standard / Gold / Platinum") filtered
              on a field that does not exist. */}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
            Owner
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              style={{ height: '36px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', color: '#374151', backgroundColor: '#fff', cursor: 'pointer' }}
            >
              <option value="All">All</option>
              {ownerOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              <option value={NO_OWNER}>No owner</option>
            </select>
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
            State
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              style={{ height: '36px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', color: '#374151', backgroundColor: '#fff', cursor: 'pointer' }}
            >
              <option value="All">All</option>
              {stateOptions.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </label>
        </div>

        {/* Table */}
        <style>{`
          /* Row separators live on the cells because a separated-border table
             does not paint borders on <tr>. */
          .partners-table thead th { border-bottom: 1px solid #e5e7eb; }
          .partners-table tbody td { border-bottom: 1px solid #f3f4f6; }
          .partners-table tbody tr:last-child td { border-bottom: none; }
        `}</style>
        {/* ⚠️ overflowX: 'auto', NOT overflow: 'hidden'. This container clipped —
            silently. The table is eleven columns of nowrap headers, and 'hidden'
            cut the last ones off at the right margin with no scrollbar and no
            way to reach them: the Resend and Cancel buttons on an invite row
            were rendered, correct, and unreachable. 'hidden' was there to clip
            the rounded corners; it also clipped the content, which is the part
            nobody notices until a column is added.

            ⚠️ AND minWidth ON THE TABLE, or width:100% just crushes the columns
            into each other instead of scrolling — a table that "fits" by
            becoming unreadable is the same bug wearing a different hat. */}
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflowX: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          {/* borderCollapse:'separate' with the row separators on the cells.
              Introduced for a sticky actions column that no longer exists; kept
              because it is equivalent and re-touching border logic to undo a
              cosmetic decision is churn with a chance of a new bug. */}
          <table className="partners-table" style={{ width: '100%', minWidth: 1180, borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                {/* ⚠️ ONLY THE NUMERIC COLUMNS SORT. Sorting Company or Status
                    alphabetically is a different feature with different edge
                    cases (nulls, case, the "Type not set" badge); offering an
                    arrow that does nothing useful is worse than not offering it. */}
                {/* 🔑 EVERY COLUMN SORTS NOW — see SORT_COLUMNS above for why the
                    text ones became safe to offer. */}
                {[['Partner', 'name'], ['Type', 'type'], ['Company', 'company'],
                  ['Owner', 'owner'], ['Status', 'status'], ['State', 'state'],
                  ['Files', 'referrals'], ['Closings', 'closings'], ['Volume', 'revenue'],
                  ['Last Activity', 'last_activity']].map(([h, key]) => (
                  <th key={h} onClick={() => toggleSort(key)}
                    style={{ padding: '11px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: sortKey === key ? '#3a6ad9' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
                      }}>
                    {h}
                    <span style={{ marginLeft: 4, color: sortKey === key ? '#3a6ad9' : '#d1d5db' }}>
                      {sortKey === key ? (sortDir === 'desc' ? '▼' : '▲') : '⇅'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="10" style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Loading…</td></tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr><td colSpan="10" style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No partners found.</td></tr>
              )}
              {!loading && sorted.map((p) => (
                <tr
                  key={p.id}
                  // ⚠️ AN INVITE ROW HAS NO DETAIL TO OPEN. The drawer reads
                  // partner_users by id; an invite has no such row, so opening it
                  // would show an empty shell built from the list fields.
                  onClick={() => openPartner(p)}
                  style={{ cursor: 'pointer', transition: 'background 0.1s', backgroundColor: '#fff' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {p.profile_picture_url ? (
                        <img src={p.profile_picture_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid #e5e7eb' }} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#3a6ad9', flexShrink: 0 }}>
                          {((p.name || p.first_name || '?')[0]).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim()}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{p.email}</div>
                        {/* 🚨 THE INVITE ACTIONS LIVE HERE, IN THE FIRST COLUMN,
                            AFTER TWO FAILED ATTEMPTS TO RESCUE THEM FROM THE LAST
                            ONE. They were an eleventh column at the right margin:
                            first clipped by overflow:hidden, then reachable-but-
                            still-off-screen behind a scrollbar, then pinned with
                            position:sticky — which floated them over the columns
                            they were meant to sit beside.

                            The first column is the one column that is never cut
                            off, never scrolled past and needs no stacking context.
                            Moving them also DELETES a column from an eleven-column
                            table, which is what created the width pressure in the
                            first place. Fixing the layout was the wrong shape of
                            answer; the column should not have been there. */}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>
                    {(p.type || p.partner_type) ? (p.type || p.partner_type).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : <span style={{ background: '#fff7ed', color: '#b45309', padding: '1px 6px', borderRadius: 6, fontWeight: 700 }}>Type not set</span>}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '12px', color: '#374151', maxWidth: '140px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.company || p.brokerage_name || p.builder_company_name || '—'}</div>
                  </td>
                  {/* ⚠️ Legacy `partners` rows have no partner_users id and so
                      can never have an assignment — they render "Not on record",
                      which is the truth rather than a blank cell. */}
                  <td style={{ padding: '12px 16px', fontSize: '12px' }}>
                    <OwnerCell name={p.owner_name} hasRow={p.has_owner_row} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <StatusBadge status={p.status} styles={STATUS_STYLES} />
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '12px', color: '#374151' }}>{p.state || p.license_state || '—'}</td>
                  <td data-testid="partner-file-count" style={{ padding: '12px 16px', fontSize: '13px', color: '#374151', textAlign: 'center' }}>
                    {/* 🔑 FILES = referrals + broker submissions (utils/partnerFileCounts), named by the partner's type. */}
                    <div>{p.referrals == null ? '—' : p.referrals}</div>
                    {p.referrals != null && (p.type || p.partner_type) && (
                      <div style={{ fontSize: '10px', color: '#9ca3af' }}>{filesLabelFor(p.type || p.partner_type).toLowerCase()}</div>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151', textAlign: 'center' }}>{p.closings}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#111827' }}>{fmtRevenue(p.revenue)}</td>
                  {/* ── 🚨 WHAT THE PARTNER DID, NOT WHEN THEIR ROW CHANGED ──
                      This rendered `updated_at || created_at`, which is the last
                      write to partner_users. Chris Brosig messaged on 1 Oct and
                      this said 30 Sep; Toni Cramond's said 2 Oct while her last
                      real activity was 15 April, because a staff edit had
                      touched her row. And twelve of twenty-three partners have
                      no activity at all — the fallback to created_at gave every
                      one of them a date.

                      🔑 THE LABEL TRAVELS WITH THE DATE. "19 Sep" tells a rep
                      the partner was alive; "Messaged Doorly" tells them what to
                      do next. */}
                  <td style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>
                    {p.last_activity_at ? (
                      <>
                        {fmtDate(p.last_activity_at)}
                        {p.last_activity_label && (
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {p.last_activity_label}
                          </div>
                        )}
                      </>
                    ) : (
                      /* ⚠️ "—", NOT THE SIGNUP DATE. A partner who has never done
                         anything has no activity date, and inventing one is the
                         defect this replaced. The registration date is still on
                         the row's detail panel for anyone who needs it. */
                      <span title="No partner-initiated activity recorded">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right Drawer */}
      <RightDrawer
        open={!!selectedPartner}
        onClose={() => setSelectedPartner(null)}
        title={selectedPartner?.name || ''}
        width={600}
      >
        {selectedPartner && (
          <div>
            {/* Sub-header */}
            {/* ⚠️ HORIZONTAL DROPPED — the drawer supplies it; see RightDrawer. */}
            <div style={{ padding: '12px 0 0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>{selectedPartner.company}</span>
              <StatusBadge status={selectedPartner.status} styles={STATUS_STYLES} />
            </div>

            {/* Tab Bar */}
            <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #e5e7eb', marginTop: '12px', paddingLeft: '20px', overflowX: 'auto' }}>
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  style={{ padding: '10px 14px', fontSize: '13px', fontWeight: '600', border: 'none', borderBottom: activeTab === t ? '2px solid #3a6ad9' : '2px solid transparent', backgroundColor: 'transparent', color: activeTab === t ? '#3a6ad9' : '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap', textTransform: 'capitalize' }}
                >
                  {t === 'referrals' ? filesLabelFor(selectedPartner.type || selectedPartner.partner_type) : t}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div style={{ padding: '20px' }}>

              {/* OVERVIEW TAB */}
              {selectedPartner?.onboarding_status === 'archived' && (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', margin: '0 0 12px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Archived</div>
                  {/* Nulls render as an explicit "unknown", never blank. Four partners
                      were archived before this was recorded, and a blank cell would
                      read as "no reason given" rather than "we never captured one". */}
                  <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                    Reason: {selectedPartner.archive_reason || <em style={{ color: '#9ca3af' }}>unknown — archived before this was recorded</em>}<br />
                    By: {selectedPartner.archived_by_name || <em style={{ color: '#9ca3af' }}>unknown</em>}
                    {selectedPartner.archived_at ? ` · ${new Date(selectedPartner.archived_at).toLocaleDateString()}` : ''}
                  </div>
                </div>
              )}
              {/* ── OWNERSHIP ─────────────────────────────────────────────
                  ⚠️ THE PREVIEW IS THE SAFEGUARD, NOT A NICETY. No mechanical
                  in-flight commission rule exists at reassignment (three were
                  considered and declined — see backend/utils/partnerHandover.js)
                  on the grounds that reassignment is admin-only and the person
                  doing it can see the specific case. That only holds if they are
                  shown it. Removing this block removes the basis for that
                  decision, not just a panel. */}
              {activeTab === 'overview' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                    Partner Success owner
                  </div>
                  {ownership === null ? (
                    <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>
                  ) : ownership.error ? (
                    <div style={{ fontSize: 13, color: '#92400e' }}>Couldn’t load ownership ({ownership.error}).</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>
                        {/* Three distinct states. "No assignment row" is NOT the
                            same as "held by nobody": the first means this partner
                            predates the ownership history, so who held them at any
                            past closing is unanswerable from data. */}
                        {!ownership.hasAssignmentRow
                          ? <span style={{ color: '#b45309' }}>No ownership on record — predates the assignment history</span>
                          : ownership.currentHolder?.name
                            ? ownership.currentHolder.name
                            : <span style={{ color: '#b45309' }}>Held by nobody (recorded explicitly)</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.6 }}>
                        {ownership.inFlight?.total || 0} file{(ownership.inFlight?.total || 0) === 1 ? '' : 's'} past submission
                        {' · '}{ownership.openTicketCount || 0} open ticket{(ownership.openTicketCount || 0) === 1 ? '' : 's'}
                      </div>
                      {/* ── 🚨 ADMINS ONLY, AND THE BUTTON NO LONGER RENDERS
                          FOR ANYONE ELSE ─────────────────────────────────────
                          This rendered for every role that could open the
                          drawer — including Partner Success, whose whole job
                          this panel is — and 403'd on click. That is the
                          control-a-role-cannot-use shape the staff-access
                          cleanup removed everywhere else, and it was the last
                          one left on this page.

                          🔑 THE SERVER IS THE GATE: the route carries an
                          explicit admin check. This stops the product offering
                          what the server will refuse. */}
                      {isAdminStaff ? (
                        <button type="button" onClick={() => { setReassignOpen(true); setReassignTo(''); setReassignErr(''); }}
                          style={{ marginTop: 10, padding: '6px 12px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          Reassign
                        </button>
                      ) : (
                        /* ⚠️ SAID, NOT LEFT BLANK. A rep who has always seen a
                           Reassign button here will look for it; "ask an admin"
                           is an answer, and an empty space is not. */
                        <div style={{ marginTop: 10, fontSize: 11.5, color: '#9ca3af', lineHeight: 1.5 }}>
                          Only an admin can change partner ownership — ask one if this needs moving.
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'overview' && (
                <>
                <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <InfoRow label="Email" value={selectedPartner.email} />
                  <InfoRow label="Phone" value={displayPhone(selectedPartner.phone)}
                    href={telHref(selectedPartner.phone)} />
                  <InfoRow label="Role" value={selectedPartner.type} />
                  <InfoRow label="State" value={selectedPartner.state} />
                  {/* 🚨 ONE ROW, LABELLED BY REGISTRY — THIS WAS TWO AND BOTH WERE
                      MISLEADING. license_number is the legacy mirror holding
                      whichever registry applies, so a loan officer's NMLS id sat
                      under "License" while the separate "NMLS" row read a field
                      the query never sent and always said N/A. Staff were told the
                      partner had a license and no NMLS id, the reverse of the
                      truth.

                      ⚠️ THE STATE IS SHOWN ONLY WHERE IT MEANS SOMETHING. An NMLS
                      id is national; tagging it "TX" would assert a jurisdiction
                      it does not have. The server decides, from the registry. */}
                  {/* 🚨 "NMLS ID" DID NOT SAY WHOSE. A loan officer's firm has its own
                      NMLS registration, so one row labelled "NMLS ID" beside a
                      single number left a rep unable to tell which of the two
                      was recorded. The label now names the subject; the firm's
                      number lives in the licence panel below and on the Profile
                      tab. */}
                  <InfoRow
                    label={selectedPartner.license_label
                      ? `Individual ${selectedPartner.license_label}`
                      : 'Individual license'}
                    value={selectedPartner.license}
                    note={selectedPartner.license_jurisdiction
                      ? `${stateName(selectedPartner.license_jurisdiction)} · state registry`
                      : selectedPartner.license_registry === 'nmls'
                        ? 'NMLS — national registry'
                        : null} />
                  <InfoRow label="W-9 Status" value={selectedPartner.w9} />
                  <InfoRow label="Agreement" value={selectedPartner.agreement_signed ? `Signed${selectedPartner.agreement_signed_at ? ' · ' + fmtDate(selectedPartner.agreement_signed_at) : ''}` : 'Not Signed'} />
                  {/* ── 🔑 TRAINING, BESIDE THE AGREEMENT ────────────────────
                      Both are things a partner must finish before they are
                      fully set up, and the drawer showed only one of them —
                      so "why is this partner still pending?" needed a second
                      screen to answer.

                      🚨 THREE STATES, NOT TWO. "Completed" with a date, and
                      otherwise the onboarding status distinguishes somebody
                      who has started the deck from somebody who has not
                      reached it — collapsing both into "Not started" would
                      say something false about twelve partners sitting at
                      training_pending. */}
                  <InfoRow label="Training"
                    value={selectedPartner.training_completed
                      ? `Completed${selectedPartner.training_completed_at
                        ? ' · ' + fmtDate(selectedPartner.training_completed_at) : ''}`
                      : selectedPartner.onboarding_status === 'training_pending' ? 'Pending'
                        : selectedPartner.onboarding_status === 'archived' ? '—'
                          : 'Not started'}
                    note={selectedPartner.training_completed
                      && !selectedPartner.training_completed_at
                      // ⚠️ COMPLETED WITH NO DATE IS ITS OWN ANSWER. The flag
                      // predates the timestamp column, so the earliest
                      // completions have one and not the other; saying so beats
                      // rendering "Completed ·" with nothing after it.
                      ? 'completed before the date was recorded' : null} />
                  {/* 🔑 SAME SOURCE AS THE COLUMN. Two answers to "when were
                      they last active" on one screen is how a rep learns to
                      distrust both. */}
                  <InfoRow label="Last Activity"
                    value={selectedPartner.last_activity_at
                      ? `${fmtDate(selectedPartner.last_activity_at)}${selectedPartner.last_activity_label ? ` — ${selectedPartner.last_activity_label}` : ''}`
                      : 'None recorded'} />
                  <InfoRow label="Registered" value={fmtDate(selectedPartner.created_at)} />
                  {/* ── 🚨 WHERE THIS PARTNER CAME FROM ─────────────────────────
                      ⚠️ ABSENCE IS NOT "ORGANIC". Only partners who arrived
                      through the lead funnel have a lead behind them; everybody
                      who predates it has none. Showing "Organic" for those would
                      assert they came to Doorly unprompted — a claim about how
                      the business grew, made out of a missing row. It is the
                      failure the Leads page already had, where every direct
                      invite read "Organic" under notes naming the rep who sent
                      it. Four states, rendered as four. */}
                  <InfoRow
                    label="Source"
                    value={
                      origin === null ? 'Loading…'
                      : origin.error ? 'Couldn’t load'
                      : !origin.found ? 'No lead on record'
                      : (SOURCE_LABEL[origin.leadSource] || origin.leadSource || 'Unknown')
                    }
                    note={
                      origin === null || origin.error ? null
                      : !origin.found ? origin.detail
                      : [
                          // 🔑 WHOSE INVITE. "Direct invite" answers half the
                          // question a rep is asking; the other half is whose.
                          origin.invitedByStaff ? `Invited by ${origin.invitedByStaff}` : null,
                          origin.invitedByPartner ? `Invited by ${origin.invitedByPartner} (partner)` : null,
                          origin.capturedAt ? `Captured ${fmtDate(origin.capturedAt)}` : null,
                        ].filter(Boolean).join(' · ') || null
                    } />
                </div>

                {/* ── 🚨 TWO CLICKS FROM THE PARTNER'S RECORD ─────────────────
                    The recording routes shipped with no UI at all: POST
                    /partners/nmls-verification and its state-register sibling
                    have existed since 1 Oct and nothing in the frontend called
                    either, so the manual verification a payout now depends on
                    was reachable only by writing a curl command. It sits here
                    because this is where a rep already is when the question
                    comes up — open the partner, record the check. */}
                {/* ── 🚨 THE PARTNER'S OWN ACTION ITEMS, MIRRORED ─────────────
                    Rendered from getPartnerTaskState — the same function the
                    partner's dashboard and Eva read. A rep answering "why is
                    this partner still pending?" was previously reading a
                    different set of facts from the one on the partner's screen.
                    Payout readiness is a separate line, filtered through
                    PAYOUT_REQUIREMENTS, because outstanding and payout-blocking
                    are not the same list. */}
                <PartnerOverviewState
                  partnerId={selectedPartner.id}
                  token={localStorage.getItem('doorly_admin_token')} />

                <div id="licence-verification">
                  <LicenseVerificationPanel
                    partnerId={selectedPartner.id}
                    token={localStorage.getItem('doorly_admin_token')} />
                </div>

                {/* 🚨 WHO SIGNS FOR THE FIRM. A partner who registered before
                    individual and firm accounts were separated has account_role
                    NULL even when they ARE the broker — and until now nothing
                    could change that, so their dashboard told them their fees
                    were held waiting for a broker who is themselves. */}
                <BrokerOfRecordField
                  partner={selectedPartner}
                  token={localStorage.getItem('doorly_admin_token')}
                  onGranted={() => {
                    setSelectedPartner(p => ({ ...p, account_role: 'broker' }));
                    setPartners(prev => prev.map(p => (p.id === selectedPartner.id
                      ? { ...p, account_role: 'broker' } : p)));
                  }} />
                </>
              )}

              {/* REFERRALS TAB */}
              {activeTab === 'referrals' && partnerFiles && (() => {
                // 🔑 THE ONE LIST (utils/partnerFileCounts): referrals AND broker submissions, each with the server's
                // link — the borrower file once registered, the submission view while it awaits the borrower.
                const label = filesLabelFor(selectedPartner.type || selectedPartner.partner_type);
                if (partnerFiles.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: '13px' }}>
                      No {label.toLowerCase()} yet.
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {partnerFiles.map((r) => {
                      const name = `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email || '—';
                      const awaiting = r.kind === 'submission' && !r.borrower_user_id;
                      return (
                        <div key={`${r.source}-${r.id}`} data-testid="partner-file-row" data-kind={r.kind}
                          onClick={() => { if (r.link) navigate(r.link); }}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 14px', backgroundColor: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb', cursor: r.link ? 'pointer' : 'default' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{name}{r.loan_number ? <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>{r.loan_number}</span> : ''}</div>
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{r.email || '—'} · {fmtDate(r.created_at)}</div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: 2 }}>
                              {r.kind === 'submission' ? `Submission #${r.submission_id || r.id}` : 'Referral'}
                              {awaiting ? ' · Awaiting borrower registration' : r.borrower_user_id ? '' : ' · not registered yet'}
                            </div>
                          </div>
                          <StatusBadge status={awaiting ? 'pending' : (r.application_status || 'pending')} styles={REFERRAL_STATUS_STYLES} />
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {activeTab === 'referrals' && !partnerFiles && (() => {
                const refs = partnerReferrals;
                if (refs.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: '13px' }}>
                      No referrals yet.
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {refs.map((r, i) => {
                      const displayName = r.borrower_first_name
                        ? `${r.borrower_first_name} ${r.borrower_last_name || ''}`.trim()
                        : `${r.first_name} ${r.last_name}`.trim();
                      const displayEmail = r.borrower_email || r.email;
                      const originalName = r.borrower_first_name && `${r.first_name} ${r.last_name}`.trim() !== displayName
                        ? `${r.first_name} ${r.last_name}`.trim() : null;
                      return (
                      <div key={r.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', backgroundColor: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{displayName}{r.borrower_loan_number ? <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>{r.borrower_loan_number}</span> : ''}</div>
                          {originalName && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>Referred as: {originalName}</div>}
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{displayEmail} · {fmtDate(r.created_at)}</div>
                          {r.borrower_status && <div style={{ fontSize: '11px', color: '#3a6ad9', fontWeight: 600, marginTop: 2 }}>{r.borrower_status.replace(/_/g, ' ')}</div>}
                        </div>
                        <StatusBadge status={r.borrower_status || r.display_status || r.status || 'pending'} styles={REFERRAL_STATUS_STYLES} />
                      </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* PAYOUTS TAB */}
              {activeTab === 'payouts' && (() => {
                const total = selectedPartner.revenue;
                const paid = Math.round(total * 0.7);
                const pending = total - paid;
                return (
                  <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    {[
                      { label: 'Total Earned', value: `$${total.toLocaleString()}` },
                      { label: 'Paid Out', value: `$${paid.toLocaleString()}` },
                      { label: 'Pending', value: `$${pending.toLocaleString()}` },
                      { label: 'Last Payout', value: fmtDate(selectedPartner.lastActivity) },
                    ].map((s) => (
                      <div key={s.label} style={{ backgroundColor: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '14px 16px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{s.label}</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* AGREEMENTS TAB */}
              {activeTab === 'agreements' && (
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '14px' }}>Partner Agreement</h3>
                  {agreementData ? (
                    <>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Type</span>
                          <p style={{ fontSize: '13px', color: '#111827', marginTop: '2px' }}>
                            {agreementData.partner_type === 'loan_officer' ? 'Broker/IMB Agreement' : agreementData.partner_type === 'realtor' ? 'Partner Agreement' : 'No agreement — type not set'}
                          </p>
                        </div>
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</span>
                          <p style={{ fontSize: '13px', color: '#111827', marginTop: '2px' }}>
                            {agreementData.agreement_signed ? 'Signed' : 'Pending'}
                          </p>
                        </div>
                        {agreementData.agreement_signed_at && (
                          <div>
                            <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Signed</span>
                            <p style={{ fontSize: '13px', color: '#111827', marginTop: '2px' }}>
                              {new Date(agreementData.agreement_signed_at).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {agreementData.agreement_fields && (
                          <>
                            <div>
                              <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Name</span>
                              <p style={{ fontSize: '13px', color: '#111827', marginTop: '2px' }}>{agreementData.agreement_fields.name}</p>
                            </div>
                            <div>
                              <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Brokerage / Company</span>
                              <p style={{ fontSize: '13px', color: '#111827', marginTop: '2px' }}>{agreementData.agreement_fields.brokerage}</p>
                            </div>
                            <div>
                              <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>License / NMLS</span>
                              <p style={{ fontSize: '13px', color: '#111827', marginTop: '2px' }}>
                                {agreementData.agreement_fields.license_number || agreementData.agreement_fields.nmls_id || '—'}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                      {agreementData.signature_data && (
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>Signature</span>
                          <img
                            src={agreementData.signature_data}
                            alt="Signature"
                            style={{ border: '1px solid #e5e7eb', borderRadius: 8, maxWidth: '100%', background: '#fafafa' }}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#9ca3af' }}>
                      No agreement data available. This partner may not have registered through DoorlyPro.
                    </p>
                  )}
                </div>
              )}

              {/* MESSAGES TAB */}
              {activeTab === 'messages' && (() => {
                // ── 🚨 THE DRIP LOG, WHICH NOTHING SHOWED ────────────────────
                //
                // Two sequences reach a registered partner — the referral drip
                // and the activation sequence — and neither was visible
                // anywhere. "What have we actually sent this partner?" had no
                // answer, which matters most on the call where they say they
                // never heard from us.
                //
                // 🔑 BUILT ON email_log, NOT THE DRIP LEDGER.
                // partner_drip_emails_sent is WIPED when the eight-email cycle
                // restarts, so it holds the current lap and nothing before it —
                // a partner four laps in looks like one who received three
                // emails. email_log keeps every send with its delivery outcome.
                //
                // ⚠️ AND IT ONLY BECAME POSSIBLE ONCE THE CAMPAIGNS NAMED
                // THEMSELVES. Every row in email_log was category 'system' —
                // the same value an agreement notice carries — so a drip send
                // was indistinguishable from a transactional one. Rows sent
                // before that change do not appear here, and saying so is
                // better than quietly under-reporting.
                const fmtWhen = (d) => new Date(d).toLocaleString('en-US',
                  { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
                // Delivery is the provider's word, and it is often absent —
                // a NULL means no webhook has landed yet, NOT a failure.
                const deliveryTone = (d) => {
                  if (!d) return { label: 'sent', color: '#6b7280', bg: '#f3f4f6' };
                  if (/deliver/i.test(d)) return { label: d, color: '#166534', bg: '#dcfce7' };
                  if (/bounce|fail|complain/i.test(d)) return { label: d, color: '#b91c1c', bg: '#fee2e2' };
                  return { label: d, color: '#6b7280', bg: '#f3f4f6' };
                };
                const CAT_LABEL = { partner_drip: 'Referral drip', partner_activation: 'Activation' };
                return (
                  <div>
                    {/* ── EMAIL LOG CARD ──────────────────────────────────── */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', marginBottom: 18, background: '#fcfcfd' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Drip emails sent
                        </div>
                        {activationSteps.length > 0 && (
                          <div style={{ fontSize: 11, color: '#6b7280' }}>
                            Activation: step {activationSteps.length} of 5
                          </div>
                        )}
                      </div>

                      {dripEmails.length === 0 ? (
                        <div style={{ fontSize: 12.5, color: '#9ca3af', lineHeight: 1.6 }}>
                          No drip emails recorded for this partner.
                          {/* 🔑 THE TWO REASONS ARE DIFFERENT AND A REP SHOULD
                              KNOW WHICH. Never sent, versus sent before the
                              campaigns stamped a category and therefore not
                              attributable. Silence here would read as the first
                              when it may be the second. */}
                          <div style={{ marginTop: 5, color: '#b0b5bd' }}>
                            Sends before 28 Sep 2026 were logged without a category and cannot be
                            attributed to a sequence — this shows nothing for them.
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 260, overflowY: 'auto' }}>
                          {dripEmails.map((e, i) => {
                            const d = deliveryTone(e.delivery_status);
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, paddingBottom: 7, borderBottom: i < dripEmails.length - 1 ? '1px solid #f1f2f4' : 'none' }}>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ fontSize: 13, color: '#111827', fontWeight: 600, overflowWrap: 'anywhere' }}>{e.subject}</div>
                                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                                    {CAT_LABEL[e.category] || e.category} · {fmtWhen(e.sent_at)}
                                  </div>
                                  {e.bounce_reason && (
                                    <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 3 }}>{e.bounce_reason}</div>
                                  )}
                                </div>
                                <span style={{ flex: '0 0 auto', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: d.bg, color: d.color, textTransform: 'capitalize' }}>
                                  {d.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {msgLoading ? (
                      <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: 13 }}>Loading messages...</div>
                    ) : partnerMsgs.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: 13 }}>No messages yet. Send the first message below.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, maxHeight: 400, overflowY: 'auto' }}>
                        {partnerMsgs.map((m, i) => {
                          // 'eva' folded in defensively. Partner threads are
                          // excluded from triage v1 (partner_id IS NOT NULL is a
                          // hard gate), so Eva cannot appear here today — but the
                          // two-way form would put her on the partner's side if
                          // that scope ever widens, so it is fixed in step.
                          const isAdmin = ['support', 'admin', 'eva'].includes(m.sender_role);
                          return (
                            <div key={m.id || i} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                              <div style={{ maxWidth: '80%', padding: '10px 13px', borderRadius: isAdmin ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: isAdmin ? '#3a6ad9' : '#f3f4f6', color: isAdmin ? '#fff' : '#111827', fontSize: 13, lineHeight: '1.5' }}>
                                {/* 🔑 fp-format: a formatted staff message renders from the server's HTML (utils/messageRichText); the rest is
                                    the plain text, as always. A staff attachment opens through the signed attachment route. */}
                                {hasRichBody(m)
                                  ? <RichMessageBody msg={m} dark={isAdmin} />
                                  : <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>}
                                {m.has_attachment && m.attachment_name && (
                                  <div style={{ marginTop: 6 }}><MessageAttachment msgId={m.id} name={m.attachment_name} token={token} dark={isAdmin} /></div>
                                )}
                                <div style={{ fontSize: 10, marginTop: 4, opacity: 0.6 }}>{new Date(m.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={msgEndRef} />
                      </div>
                    )}
                    <StaffComposer key={selectedPartner.id} token={token} onSend={sendPartnerMsg} testId="partner-pane-composer"
                      placeholder="Type a message… (⌘↵ to send)" sendLabel="Send" />
                  </div>
                );
              })()}

              {/* DOCUMENTS TAB — dynamic per partner type */}
              {activeTab === 'documents' && selectedPartner && (() => {
                const pType = (selectedPartner.type || selectedPartner.partner_type || 'realtor').toLowerCase().replace(/\s+/g, '_');
                const isHomebuilder = pType === 'homebuilder';
                const isLoanOfficer = pType === 'loan_officer' || pType === 'loan officer';

                const uploadDocs = isHomebuilder ? [] : [
                  ...(!isLoanOfficer ? [{
                    type: 'real_estate_license', label: 'Real Estate License',
                    description: 'State real estate agent or broker license',
                    hasFile: !!selectedPartner.doc_real_estate_license,
                    fileName: selectedPartner.doc_real_estate_license_name,
                    uploadedAt: selectedPartner.doc_real_estate_license_uploaded_at,
                    fileData: selectedPartner.doc_real_estate_license,
                  }] : []),
                  ...(isLoanOfficer ? [{
                    type: 'mlo_license', label: 'MLO License',
                    description: 'Mortgage Loan Originator license / NMLS documentation',
                    hasFile: !!selectedPartner.doc_mlo_license,
                    fileName: selectedPartner.doc_mlo_license_name,
                    uploadedAt: selectedPartner.doc_mlo_license_uploaded_at,
                    fileData: selectedPartner.doc_mlo_license,
                  }] : []),
                  {
                    type: 'w9', label: 'W-9 Form',
                    description: 'IRS W-9 form for referral fee reporting',
                    hasFile: !!selectedPartner.doc_w9,
                    fileName: selectedPartner.doc_w9_name,
                    uploadedAt: selectedPartner.doc_w9_uploaded_at,
                    fileData: selectedPartner.doc_w9,
                  },
                ];

                const handleDocUpload = async (docType, file) => {
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('doc_type', docType);
                  setDocUploading(docType);
                  try {
                    const res = await axios.post(`${API}/api/admin/partners/${selectedPartner.id}/documents`, formData, {
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
                    });
                    if (res.data.success) {
                      await fetchPartners();
                      setDocUploadMsg('Document uploaded');
                      setTimeout(() => setDocUploadMsg(null), 3000);
                    }
                  } catch (e) { setDocUploadMsg('Error: ' + (e.response?.data?.error || e.message)); }
                  finally { setDocUploading(null); }
                };

                const handleDocDelete = async (docType) => {
                  if (!window.confirm('Remove this document?')) return;
                  try {
                    await axios.delete(`${API}/api/admin/partners/${selectedPartner.id}/documents/${docType}`, { headers: { Authorization: `Bearer ${token}` } });
                    await fetchPartners();
                  } catch (e) { console.error(e); }
                };

                return (
                  <div>
                    <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280' }}>
                      {isHomebuilder ? 'Homebuilder partners do not have document requirements.' : 'Partner compliance documents and signed agreements.'}
                    </p>

                    {docUploadMsg && (
                      <div style={{ background: docUploadMsg.startsWith('Error') ? '#fff0f3' : '#f0fdf4', border: `1px solid ${docUploadMsg.startsWith('Error') ? '#fecdd3' : '#bbf7d0'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: docUploadMsg.startsWith('Error') ? '#ff082f' : '#166534', fontWeight: 600 }}>
                        {docUploadMsg}
                      </div>
                    )}

                    {/* Signed Agreement — Realtors and LOs only */}
                    {!isHomebuilder && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                          DoorlyPro Partner Agreement
                        </div>
                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                          <div style={{ width: 38, height: 38, borderRadius: 9, background: selectedPartner.agreement_signed ? '#f0fdf4' : '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon name="document" size={18} color={selectedPartner.agreement_signed ? '#166534' : '#7a5200'} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
                              {isLoanOfficer ? 'Broker/IMB Agreement' : 'Partner Agreement'}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: selectedPartner.agreement_signed ? '#f0fdf4' : '#fff7ed', color: selectedPartner.agreement_signed ? '#166534' : '#7a5200', border: `1px solid ${selectedPartner.agreement_signed ? '#bbf7d0' : '#fed7aa'}` }}>
                                {selectedPartner.agreement_signed ? 'Signed' : 'Pending'}
                              </span>
                              {selectedPartner.agreement_signed_at && (
                                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                                  {new Date(typeof selectedPartner.agreement_signed_at === 'string' && selectedPartner.agreement_signed_at.length === 10 ? selectedPartner.agreement_signed_at + 'T12:00:00' : selectedPartner.agreement_signed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                          </div>
                          {(selectedPartner.agreement_pdf_url || selectedPartner.agreement_pdf_data) ? (
                            <a href={selectedPartner.agreement_pdf_url || selectedPartner.agreement_pdf_data}
                              target="_blank" rel="noreferrer"
                              download={`DoorlyPro-Agreement-${selectedPartner.first_name || selectedPartner.name}.pdf`}
                              style={{ padding: '7px 14px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 12, flexShrink: 0 }}>
                              Download
                            </a>
                          ) : (
                            <span style={{ padding: '7px 14px', background: '#f3f4f6', color: '#9ca3af', borderRadius: 8, fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                              {selectedPartner.agreement_signed ? 'Processing' : 'Unavailable'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Compliance Documents — per partner type */}
                    {!isHomebuilder && uploadDocs.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                          Compliance Documents
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {uploadDocs.map(doc => (
                            <div key={doc.type} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{ width: 38, height: 38, borderRadius: 9, background: doc.hasFile ? '#f0fdf4' : '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Icon name="document" size={18} color={doc.hasFile ? '#166534' : '#9ca3af'} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{doc.label}</div>
                                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                    {doc.hasFile
                                      ? `${doc.fileName || 'Document on file'}${doc.uploadedAt ? ' · ' + new Date(doc.uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}`
                                      : doc.description}
                                  </div>
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, flexShrink: 0, background: doc.hasFile ? '#f0fdf4' : '#f9fafb', color: doc.hasFile ? '#166534' : '#9ca3af', border: `1px solid ${doc.hasFile ? '#bbf7d0' : '#e5e7eb'}` }}>
                                  {doc.hasFile ? 'On file' : 'Missing'}
                                </span>
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                  {doc.hasFile && doc.fileData && (
                                    <button onClick={() => { setViewingPartnerDoc({ url: doc.fileData, name: doc.fileName }); setShowPartnerDocModal(true); }}
                                      style={{ padding: '6px 12px', background: '#eff6ff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                                      View
                                    </button>
                                  )}
                                  {doc.hasFile && (
                                    <button onClick={() => handleDocDelete(doc.type)}
                                      style={{ padding: '6px 10px', background: '#fff0f3', color: '#ff082f', border: '1px solid #fecdd3', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                                      ✕
                                    </button>
                                  )}
                                  <label style={{ padding: '6px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: 12, display: 'inline-block' }}>
                                    {docUploading === doc.type ? 'Uploading...' : doc.hasFile ? 'Replace' : 'Upload'}
                                    <input type="file" accept=".pdf,image/jpeg,image/png" style={{ display: 'none' }} disabled={docUploading === doc.type}
                                      onChange={e => { const f = e.target.files?.[0]; if (f) handleDocUpload(doc.type, f); e.target.value = ''; }} />
                                  </label>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Training completion */}
                    {!isHomebuilder && selectedPartner.onboarding_completed && (
                      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                        <div style={{ width: 38, height: 38, borderRadius: 9, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon name="check-circle" size={18} color="#166534" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 2 }}>Training Complete</div>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>Complete</span>
                        </div>
                      </div>
                    )}

                    {/* Homebuilder empty state */}
                    {isHomebuilder && (
                      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 32, textAlign: 'center' }}>
                        <div style={{ marginBottom: 10 }}><Icon name="building" size={32} color="#d1d5db" /></div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>No documents required</div>
                        <div style={{ fontSize: 13, color: '#9ca3af' }}>Homebuilder partners are not required to submit compliance documents or sign a partner agreement.</div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* PROFILE TAB */}
              {activeTab === 'profile' && selectedPartner && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Update partner profile. Changes save immediately.</p>
                  {[
                    { label: 'Name', field: 'name', type: 'text' },
                    { label: 'Email', field: 'email', type: 'email' },
                    { label: 'Phone', field: 'phone', type: 'tel' },
                    { label: 'Company / Brokerage', field: 'company', type: 'text' },
                    { label: 'State', field: 'state', type: 'text' },
                    // 🚨 NO LICENCE FIELD HERE. There was one — "License # —
                    // this person's own NMLS ID" — and it was a DUPLICATE of the
                    // Individual NMLS ID field in PartnerLicenseFields below,
                    // except that the two were not even bound to the same
                    // column: this one wrote partner_profiles.license_number,
                    // the legacy mirror, while every gate reads nmls_id (or
                    // real_estate_license_number). So a correction typed here
                    // appeared to save and changed nothing anywhere that
                    // matters. The field below is now editable and the server
                    // dispatches the write on the registry.
                  ].map(f => (
                    <div key={f.field}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                      <input type={f.type}
                        value={editForm[f.field] ?? selectedPartner[f.field] ?? ''}
                        onChange={e => setEditForm(p => ({ ...p, [f.field]: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }} />
                    </div>
                  ))}
                  {/* 🔑 PARTNER TYPE (push C). The "type not set" refusal told staff to
                      set it here and there was no field. The server validates it and
                      recomputes the partner's status from their setup after a change. */}
                  {selectedPartner.source !== 'legacy' && (
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Partner Type</label>
                      <select data-testid="partner-type-select"
                        value={editForm.type ?? (selectedPartner.partner_type || (['realtor', 'loan_officer', 'homebuilder'].includes(selectedPartner.type) ? selectedPartner.type : '')) ?? ''}
                        onChange={e => setEditForm(p => ({ ...p, type: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', outline: 'none', background: '#fff' }}>
                        <option value="">Type not set</option>
                        <option value="realtor">Realtor</option>
                        <option value="loan_officer">Loan Officer</option>
                        <option value="homebuilder">Homebuilder</option>
                      </select>
                    </div>
                  )}
                  {/* 🚨 THE TWO NUMBERS, SAID APART. For a loan officer the
                      originator and the firm hold different NMLS registrations;
                      for a realtor a registered brokerage has its own licence
                      and a sole trader may not. Shown on an account holder's
                      record because that is who controls the firm's. */}
                  <PartnerLicenseFields
                    partnerId={selectedPartner.id}
                    token={token}
                    isAccountHolder={selectedPartner.account_role === 'broker'}
                    // 🔑 THE DRAWER AND THE LIST ROW FOLLOW THE SAVE. The number
                    // is on both, read from the legacy mirror the server keeps in
                    // step — without this the row behind the drawer shows the old
                    // value until the next fetch and reads as a save that did not
                    // take.
                    onSaved={(v) => {
                      setPartners(prev => prev.map(p => (p.id === selectedPartner.id
                        ? { ...p, license: v } : p)));
                      setSelectedPartner(p => ({ ...p, license: v }));
                    }} />
                  {editSaveMsg && (
                    <div style={{ background: editSaveMsg.startsWith('✓') ? '#f0fdf4' : '#fff0f3', border: `1px solid ${editSaveMsg.startsWith('✓') ? '#bbf7d0' : '#fecdd3'}`, borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: editSaveMsg.startsWith('✓') ? '#166534' : '#ff082f', fontWeight: '600' }}>
                      {editSaveMsg}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => { setEditForm({}); setEditSaveMsg(null); }}
                      style={{ flex: 1, padding: '10px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>Reset</button>
                    <button disabled={Object.keys(editForm).length === 0} onClick={async () => {
                      try {
                        const res = await axios.put(`${API}/api/admin/partners/${selectedPartner.id}`, { ...editForm, source: selectedPartner.source }, { headers: { Authorization: `Bearer ${token}` } });
                        if (res.data.success) {
                          setEditSaveMsg('✓ Changes saved');
                          setPartners(prev => prev.map(p => p.id === selectedPartner.id ? { ...p, ...editForm } : p));
                          setSelectedPartner(p => ({ ...p, ...editForm }));
                          setTimeout(() => setEditSaveMsg(null), 3000);
                        }
                      } catch (e) { setEditSaveMsg('Error: ' + (e.response?.data?.error || e.message)); }
                    }}
                      style={{ flex: 2, padding: '10px', background: Object.keys(editForm).length === 0 ? '#e5e7eb' : '#3a6ad9', color: Object.keys(editForm).length === 0 ? '#9ca3af' : '#fff', border: 'none', borderRadius: '8px', cursor: Object.keys(editForm).length === 0 ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '13px' }}>
                      Save Changes
                    </button>
                  </div>
                </div>
              )}

              {/* ARCHIVE TAB */}
              {activeTab === 'archive' && selectedPartner && (
                <div>
                  <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: '12px', padding: '16px 18px', marginBottom: '18px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#ff082f', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}><Icon name="warning" size={16} color="#ff082f" /> Archive This Partner</div>
                    <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
                      Archiving will deactivate <strong>{selectedPartner.name || `${selectedPartner.first_name} ${selectedPartner.last_name}`}</strong>'s DoorlyPro account. They will no longer be able to log in or submit referrals.
                    </p>
                  </div>
                  {archiveConfirm ? (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => setArchiveConfirm(false)}
                        style={{ flex: 1, padding: '11px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                      <button onClick={async () => {
                        try {
                          // Reason is REQUIRED by the endpoint. Four partners are
                          // already archived with no reason on record; this is what
                          // stops that happening again.
                          const reason = window.prompt('Why is this partner being archived? (required — recorded in the audit log)');
                          if (!reason || !reason.trim()) return;
                          await axios.patch(`${API}/api/admin/partners/doorlypro/${selectedPartner.id}/status`, { status: 'archived', reason: reason.trim() }, { headers: { Authorization: `Bearer ${token}` } });
                          setSelectedPartner(null);
                          setArchiveConfirm(false);
                          setActiveTab('overview');
                          fetchPartners();
                        } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
                      }}
                        style={{ flex: 2, padding: '11px', background: '#ff082f', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '14px' }}>
                        Yes, Archive Partner
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setArchiveConfirm(true)}
                      style={{ width: '100%', padding: '12px', background: '#fff0f3', color: '#ff082f', border: '2px solid #fecdd3', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>
                      Archive This Partner
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </RightDrawer>

      {/* ── REASSIGN CONFIRM ────────────────────────────────────────────────
          ⚠️ THIS SHOWS WHAT THE MOVE TAKES WITH IT, BY STAGE, BEFORE CONFIRMING.
          There is no automatic split, tail or freeze for files in flight at a
          handover — all three were considered and declined because reassignment
          is admin-only and the human doing it has the context. This panel IS that
          context. A confirm step that does not show it makes the declined rule
          indefensible. */}
      {reassignOpen && selectedPartner && (
        <div onClick={() => !reassignBusy && setReassignOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 10000 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, maxWidth: 460, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', padding: 28 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#111827' }}>Reassign {selectedPartner.name}</h2>
            <p style={{ margin: '0 0 18px', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
              The account, its open tickets and everything in flight move to the new owner.
              Resolved tickets stay where they are as history.
            </p>

            {ownership && !ownership.error && (
              <div style={{ marginBottom: 18, padding: '12px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                {(ownership.inFlight?.total || 0) === 0 ? (
                  // ⚠️ "past submission" IS LOAD-BEARING COPY. Pre-application
                  // files are deliberately not counted, so a partner with active
                  // borrowers can legitimately read zero. Saying "no files" here
                  // would be a different and false claim.
                  <span style={{ color: '#6b7280' }}>Nothing past submission is in flight for this account.</span>
                ) : (
                  <>
                    <strong>{ownership.inFlight.total}</strong> file{ownership.inFlight.total === 1 ? '' : 's'} past submission move with it:
                    <div style={{ marginTop: 6 }}>
                      {(ownership.inFlight.byStage || []).map((r) => (
                        <div key={r.stage} style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
                          <span style={{ textTransform: 'capitalize' }}>{String(r.stage).replace(/_/g, ' ')}</span>
                          <span>{r.n}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <div style={{ color: '#6b7280', marginTop: 8 }}>
                  {ownership.openTicketCount || 0} open ticket{(ownership.openTicketCount || 0) === 1 ? '' : 's'} move too.
                </div>
                {/* ⚠️ NAMED, because commission on a closing follows whoever held
                    the account AT THE CLOSING. Moving it now moves that. */}
                <div style={{ color: '#92400e', marginTop: 8, lineHeight: 1.6 }}>
                  Commission on anything that closes after this move goes to the new owner.
                </div>
              </div>
            )}

            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>New owner</label>
            <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, background: '#fff', color: '#111827' }}>
              <option value="">Select someone…</option>
              {(ownership?.candidates || [])
                .filter((u) => u.id !== ownership?.currentHolder?.id)
                .map((u) => (
                  <option key={u.id} value={u.id}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</option>
                ))}
            </select>

            {reassignErr && (
              <div style={{ fontSize: 13, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginTop: 14, lineHeight: 1.6 }}>
                {reassignErr}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button type="button" onClick={() => setReassignOpen(false)} disabled={reassignBusy}
                style={{ padding: '9px 16px', background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="button" onClick={submitReassign} disabled={reassignBusy || !reassignTo}
                style={{ padding: '9px 18px', background: (reassignBusy || !reassignTo) ? '#9ca3af' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: (reassignBusy || !reassignTo) ? 'not-allowed' : 'pointer' }}>
                {reassignBusy ? 'Moving…' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {showPartnerDocModal && viewingPartnerDoc?.url && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10002, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box', boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{viewingPartnerDoc.name || 'Document'}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <a href={viewingPartnerDoc.url} download={viewingPartnerDoc.name || 'document'}
                  style={{ padding: '7px 14px', background: '#3a6ad9', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>Download</a>
                <button onClick={() => { setShowPartnerDocModal(false); setViewingPartnerDoc(null); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1, padding: '4px' }}>✕</button>
              </div>
            </div>
            <div style={{ flex: 1, background: '#f3f4f6', minHeight: 500 }}>
              {viewingPartnerDoc.url.startsWith('data:image') ? (
                <img src={viewingPartnerDoc.url} alt="Document" style={{ width: '100%', height: 'auto', display: 'block' }} />
              ) : (
                <iframe src={viewingPartnerDoc.url} title="Document" style={{ width: '100%', height: '100%', minHeight: 500, border: 'none' }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Partner Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontWeight: '700', fontSize: '16px', color: '#111827', margin: 0 }}>Add Partner</h3>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '20px' }}>×</button>
            </div>
            <form onSubmit={handleAddSave} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { label: 'Full Name', key: 'name', required: true },
                { label: 'Company / Brokerage', key: 'company' },
                { label: 'Email', key: 'email', type: 'email', required: true },
                { label: 'Phone', key: 'phone' },
              ].map(({ label, key, type, required }) => (
                <div key={key}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>{label}{required && <span style={{ color: '#ff082f' }}> *</span>}</label>
                  <input
                    type={type || 'text'}
                    required={required}
                    value={addForm[key]}
                    onChange={(e) => setAddForm((f) => ({ ...f, [key]: e.target.value }))}
                    style={{ width: '100%', height: '38px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
              <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>State</label>
                  {/* ⚠️ A ONE-OPTION DROPDOWN IS A GATE, NOT A DISPLAY. This was
                      hardcoded <option value="TX"> — correct today and a literal
                      that will not follow the licence. Derived from the mirror,
                      which is also what makes a second licensed state appear
                      here without anyone remembering this file exists. */}
                  <select value={addForm.state} onChange={(e) => setAddForm((f) => ({ ...f, state: e.target.value }))} style={{ width: '100%', height: '38px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' }}>
                    {LICENSED_STATE_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Status</label>
                  <select value={addForm.status} onChange={(e) => setAddForm((f) => ({ ...f, status: e.target.value }))} style={{ width: '100%', height: '38px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' }}>
                    {['pending', 'active', 'suspended', 'inactive'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Notes</label>
                <textarea
                  value={addForm.notes}
                  onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '4px' }}>
                <button type="button" onClick={() => setShowAddModal(false)} style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', backgroundColor: '#3a6ad9', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Saving…' : 'Save Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite Partner Modal */}
      {/* Cancel-invite confirm. ⚠️ Says what cancelling does and does NOT do —
          the invite is withdrawn, the record of the approach is kept, and the
          person can be invited again. Without that, "Cancel" reads as "delete
          this person" and reps hesitate to use it. */}

      {/* 🚨 ONE FORM, TWO PAGES. This was 180 lines of modal here and a thinner
          copy in AccountInviteModal on the Accounts page — two forms in front of
          ONE endpoint, with their own required-field lists and their own error
          handling. The Accounts page now renders the same component with
          lockKind="account". */}
      <PartnerInviteModal
        token={token}
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSent={fetchPartners}
      />

      {/* Archived Partners Modal */}
      {showArchivedModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #f3f4f6' }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>📦 Archived Partners</h3>
              <button onClick={() => setShowArchivedModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af' }}>×</button>
            </div>
            <div style={{ padding: '16px 24px' }}>
              {archivedPartners.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 14 }}>No archived partners.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      {['Partner', 'Email', 'Type', ''].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {archivedPartners.map(p => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#111827' }}>{p.name}</td>
                        <td style={{ padding: '10px 12px', color: '#6b7280' }}>{p.email}</td>
                        <td style={{ padding: '10px 12px', color: '#374151', textTransform: 'capitalize' }}>{p.type}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <button onClick={async () => {
                            // 🚨 RESTORE NO LONGER FORCES ACTIVE (push C): the server works the
                            // status out from the partner's setup checklist.
                            if (!window.confirm(`Restore ${p.name}? Their status is worked out from their setup checklist; they become Active only if every requirement is met.`)) return;
                            try {
                              const reason = window.prompt('Reason for reactivating this partner? (optional — recorded in the audit log)') || '';
                              await axios.patch(`${API}/api/admin/partners/doorlypro/${p.id}/status`, { status: 'active', reason: reason.trim() }, { headers: { Authorization: `Bearer ${token}` } });
                              setArchivedPartners(prev => prev.filter(a => a.id !== p.id));
                              fetchPartners();
                            } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
                          }} style={{ padding: '4px 12px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
